import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import LoadingState from "../../Components/LoadingState";
import { useNavigate } from "react-router-dom";
import {
  Gift,
  Rocket,
  Users,
  Building2,
  Sparkles,
  Globe,
  Bot,
  Zap,
  Check,
  ArrowRight,
  ArrowLeft,
  ArrowUpRight,
  ArrowDownRight,
  ReceiptText,
  CalendarDays,
  Lock,
  ShieldCheck,
  X,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import {
  listPlans,
  previewCheckout,
  startCheckout,
  cancelSubscription,
  retryPayment,
  formatLimit,
  planLabel,
  isUnlimited,
} from "../../actions/billing";
import { RoutesPaths } from "../../Router/constant";
import ConfirmDialog from "../../Components/ConfirmDialog";
import useHelcimPayment from "./useHelcimPayment";
import {
  ACTIONS,
  metaFor,
  isBlockedPreview,
  needsFewerAssistants,
} from "./previewMeta";

// Presentation only — the API supplies every number. A plan added later still
// renders, with the neutral icon and no blurb.
const PLAN_STYLE = {
  free: {
    icon: Gift,
    tone: "violet",
    blurb: "For trying Hostor and seeing how it works on your website.",
  },
  starter: {
    icon: Sparkles,
    tone: "blue",
    blurb: "For small businesses that need an AI receptionist on one website.",
  },
  pro: {
    icon: Rocket,
    tone: "blue",
    blurb: "For growing businesses that need Hostor across several websites.",
  },
  agency: {
    icon: Users,
    tone: "violet",
    blurb: "For agencies managing AI receptionists for multiple clients.",
  },
  business: {
    icon: Building2,
    tone: "green",
    blurb: "For larger organizations needing higher usage and custom workflows.",
  },
};

// The three headline allowances, shown boxed at the top of every card.
const HIGHLIGHTS = [
  { key: "maxAssistants", icon: Globe, one: "website", many: "websites" },
  { key: "maxAssistants", icon: Bot, one: "AI assistant", many: "AI assistants" },
  {
    key: "monthlyChatLimitPerAssistant",
    icon: Zap,
    one: "AI chat / month",
    many: "AI chats / month",
  },
];

// Everything else the plan record carries, as a checklist.
const DETAILS = [
  { key: "maxTrainingMessages", label: "training messages" },
  { key: "maxVisitorMessages", label: "messages per visitor" },
  { key: "maxKnowledgeSources", label: "pages crawled (whole account)" },
  { key: "maxRecrawlsPerMonth", label: "re-crawls / month per assistant" },
  { key: "maxLeadsPerMonth", label: "leads / month" },
];

// "$23.20" but "$23" — trailing zeroes on a price read as false precision.
const price = (value) => {
  if (typeof value !== "number") return null;
  return `$${Number.isInteger(value) ? value : value.toFixed(2)}`;
};

// Line items arrive signed - a credit for unused time is negative - so the
// sign is kept rather than shown as a bare number.
const signed = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";

  return `${amount < 0 ? "−" : ""}$${Math.abs(amount).toFixed(2)}`;
};

// "2026-09-22" -> "22 Sep 2026", read as a plain date rather than a moment.
const plainDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  if (!match) return "";

  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString(
    undefined,
    { day: "numeric", month: "short", year: "numeric" },
  );
};

// Served from public/, so the path is a plain URL rather than an import: the
// same convention the logos use. It is set here instead of in the stylesheet
// because css-loader tries to resolve a url() as a module and fails the build.
// React style keys are camelCase JS identifiers, not CSS property names -
// `background-size` is a subtraction, and object-fit does nothing to a
// background anyway (it is for the content of an img or video).
const PANEL_BACKGROUND = {
  backgroundImage: 'url("/assets/bgpreivew.png")',
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
};

const allowance = (value, one, many) => {
  if (isUnlimited(value)) return `Unlimited ${many}`;
  if (typeof value !== "number") return null;
  return `${value.toLocaleString()} ${value === 1 ? one : many}`;
};

export default function ChangePlanModal({
  open,
  currentPlan,
  // Which cycle that plan is on. Without it the same tier looks like the
  // current one on both toggles, and the card for the cycle the account is
  // *not* on cannot be clicked - which is the whole move from monthly to
  // annual.
  currentInterval = "",
  // Whether that plan is still giving access. A subscription that has ended,
  // or one paused for an unpaid renewal, leaves the account with the name of
  // a plan but nothing behind it - and the way back is to buy it again, which
  // means every card has to be choosable, including the one it used to be on.
  planActive = true,
  onClose,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // The card popup and the call that confirms what it reports.
  const { pay, stage: payStage, error: payError } = useHelcimPayment();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyPlan, setBusyPlan] = useState("");
  const [error, setError] = useState("");
  const [annual, setAnnual] = useState(true);
  // The costed change awaiting confirmation. Nothing is charged until the
  // customer has seen this and pressed the button on it.
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState("");
  // Turning off auto-renewal cannot be undone, so it is asked before it is
  // sent rather than fired by the button that happens to be in that slot.
  const [askCancel, setAskCancel] = useState(false);
  // Money is about to move, so the button asks before it does - the same way
  // turning off auto-renewal does.
  const [askPay, setAskPay] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await dispatch(listPlans());
    setLoading(false);

    if (!result?.success) {
      setError(result?.message || "We couldn't load the available plans.");
      return;
    }

    setError("");
    setPlans(result.plans);
  }, [dispatch]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Derived from the two prices rather than stated anywhere, so the badge can
  // never disagree with what a card actually charges.
  const savingPct = plans.reduce((best, plan) => {
    if (!plan.monthlyPrice || typeof plan.annualPriceMonthly !== "number") {
      return best;
    }

    const pct = Math.round((1 - plan.annualPriceMonthly / plan.monthlyPrice) * 100);
    return Math.max(best, pct);
  }, 0);

  const interval = annual ? "annual" : "monthly";

  // Step one: what would this actually cost? Moving straight to checkout
  // would charge a prorated amount the customer has never been shown.
  const choose = async (plan) => {
    setError("");
    setPreviewing(plan.plan);

    const result = await dispatch(previewCheckout(plan.plan, interval));

    setPreviewing("");

    if (!result?.success) {
      setError(result?.message || "We couldn't work out what this would cost.");
      return;
    }

    setPreview({ ...result.preview, planRecord: plan });
  };

  // Deleting is done where the assistant lives, on Install & Settings: it
  // takes a widget off a live website, which is a decision to make in front of
  // that assistant rather than inside a billing dialog.
  const goToInstallSettings = () => {
    onClose();
    navigate(RoutesPaths.INSTALL_SETTINGS);
  };

  // Step two: the customer has seen the amount and agreed to it. What that
  // means depends on the case the preview came back as - buying the plan,
  // paying a renewal that failed, or turning off auto-renewal so a downgrade
  // becomes possible later. The server decides which; this only dispatches it.
  const confirm = async () => {
    const plan = preview?.to?.plan || preview?.newPlan?.plan || preview?.plan;
    const intent = metaFor(preview).action;

    // Nothing to send for a plan the account is already on, or a change the
    // server has disabled - and it would answer 400 anyway. A locked
    // downgrade is disabled too, but its button does not buy the plan: it
    // turns off auto-renewal, which is the one thing the server will take.
    if (intent === ACTIONS.NONE) return;
    if (intent !== ACTIONS.CANCEL && isBlockedPreview(preview)) return;
    if (intent === ACTIONS.CHECKOUT && !plan) return;

    // A locked downgrade: the plan on screen cannot be taken during the paid
    // period, and turning off auto-renewal is the step that makes it possible
    // once the period ends. It stops every future payment and cannot be
    // undone, so it is confirmed before anything is sent.
    if (intent === ACTIONS.CANCEL) {
      setError("");
      setAskCancel(true);
      return;
    }

    setError("");
    setAskPay(true);
  };

  // Answered yes to the question above: take the payment.
  const runPayment = async () => {
    const plan = preview?.to?.plan || preview?.newPlan?.plan || preview?.plan;
    const intent = metaFor(preview).action;

    setAskPay(false);
    setBusyPlan(plan || preview?.type || "busy");

    // A failed renewal has to be paid before anything else can be bought, so
    // this button pays that rather than the plan on screen.
    const result =
      intent === ACTIONS.RETRY
        ? await dispatch(retryPayment())
        : await dispatch(
            startCheckout(
              plan,
              preview?.to?.interval || preview?.interval || interval,
            ),
          );

    if (!result?.success) {
      setBusyPlan("");
      setError(
        result?.message ||
          (intent === ACTIONS.RETRY
            ? "We couldn't start that payment."
            : "We couldn't start that plan change."),
      );
      return;
    }

    // Nothing to charge: the change was applied server-side, so there is no
    // card to take.
    if (!result.checkout) {
      setBusyPlan("");
      onClose(true);
      return;
    }

    // The card is taken in Helcim's popup, over this page rather than away
    // from it - so the modal stays mounted and picks up where it left off.
    const payment = await pay(result.checkout);

    setBusyPlan("");

    // Closed without paying: nothing has changed, and the quote is still on
    // screen to try again from.
    if (!payment.done) {
      if (payment.message) setError(payment.message);
      return;
    }

    // "processing" means the money is through but the plan is a moment
    // behind; the page that reloads behind this modal shows it either way.
    onClose(true, payment.message);
  };

  // Answered yes to the question above: stop the renewals. The plan keeps
  // working until the end of the period already paid for.
  const turnOffAutoRenewal = async () => {
    setError("");
    setBusyPlan("cancel");

    const result = await dispatch(cancelSubscription());

    setBusyPlan("");
    setAskCancel(false);

    if (!result?.success) {
      setError(result?.message || "We couldn't turn off auto-renewal.");
      return;
    }

    onClose(true);
  };

  // Field names have moved more than once; each reads the current name first
  // and falls back to what it replaced, so an older response still renders.
  const typeMeta = metaFor(preview);
  const intent = typeMeta.action;
  // Only new_subscription and resubscribe carry this: the plan holds fewer
  // assistants than the account is running, and the server will not sell it
  // until some are gone.
  const overAssistantLimit = needsFewerAssistants(preview);
  const assistantsOver = preview?.assistants;
  const toPlan = preview?.to || preview?.newPlan;
  // Named fromPlan: currentPlan is already the prop naming the tier the
  // account is on, and the two are the same thing seen from different places.
  const fromPlan = preview?.from || preview?.currentPlan;
  const targetPlan = toPlan?.plan || preview?.plan;

  const renewalDate =
    preview?.renewsOn || preview?.nextRenewalDate || preview?.nextBillingDate;

  const dueToday =
    typeof preview?.chargeToday === "number"
      ? preview.chargeToday
      : preview?.total;
  // The response no longer always carries tax; when it does not, there is
  // nothing to subtract and nothing to show.
  const taxAmount = typeof preview?.tax === "number" ? preview.tax : null;
  const hasTax = taxAmount !== null && taxAmount !== 0;
  // Not returned: the API sends the tax and the total, so the subtotal is the
  // difference rather than a third figure that could disagree with them.
  const subtotal =
    typeof dueToday === "number" && taxAmount !== null
      ? dueToday - taxAmount
      : preview?.subtotal;

  const action = preview?.action || preview?.cta;
  const actionDisabled = isBlockedPreview(preview);
  // A blocked change, or a downgrade the account cannot take yet: the message
  // is the reason, not a note about a choice that will work.
  // Everything that is a reason rather than a note about a change that works.
  const isBlocked = actionDisabled || overAssistantLimit;

  // The question itself. The type decides the verb - upgrading, subscribing,
  // paying a failed renewal - and the amount is the one the quote gives.
  const payTitle =
    preview?.type === "upgrade"
      ? `Upgrade to ${toPlan?.label || planLabel(targetPlan)}?`
      : preview?.type === "payment_failed"
        ? "Pay the failed renewal?"
        : `Subscribe to ${toPlan?.label || planLabel(targetPlan)}?`;

  const payMessage = [
    typeof dueToday === "number"
      ? `${signed(dueToday)} will be charged now.`
      : "",
    preview?.renewsOn
      ? `It renews on ${plainDate(preview.renewsOn)} unless you cancel.`
      : "",
    "You'll enter your card details on the next screen.",
  ]
    .filter(Boolean)
    .join(" ");

  const planRecord =
    preview?.planRecord || plans.find((entry) => entry.plan === targetPlan);
  const PreviewIcon = PLAN_STYLE[targetPlan]?.icon;

  // Free is never bought and Business is quoted rather than listed, so the two
  // non-purchasable tiers need different words.
  const ctaFor = (plan, isCurrent, busy, otherCycle) => {
    if (isCurrent) return "Your plan";
    if (busy) return "Checking…";
    // Same tier, other cycle: the tier is not what changes, the billing is.
    if (otherCycle) {
      return annual ? "Switch to annual billing" : "Switch to monthly billing";
    }
    if (plan.purchasable) return `Choose ${planLabel(plan.plan)}`;
    if (plan.monthlyPrice === 0) return "Start for free";

    return "Talk to sales";
  };

  const renderCard = (plan) => {
    const style = PLAN_STYLE[plan.plan] || {};
    const PlanIcon = style.icon;
    // The plan the account is on, on the cycle it is billed for. The same
    // tier on the other cycle is a change like any other - an upgrade going
    // to annual, a locked downgrade coming back - so it stays clickable.
    const samePlan = plan.plan === currentPlan;
    const isCurrent =
      planActive &&
      samePlan &&
      (!currentInterval || interval === currentInterval);
    const otherCycle = planActive && samePlan && !isCurrent;
    const available = plan.purchasable && !isCurrent;

    const headline = annual ? plan.annualPriceMonthly : plan.monthlyPrice;
    const shown = price(headline);

    // The whole card is the target, not just the button at the bottom of it:
    // the price and the limits are what someone is reading when they decide.
    // The button stays, because it is what says the card can be chosen.
    const pick = () => {
      if (!available || previewing || busyPlan) return;
      choose(plan);
    };

    return (
      <div
        key={plan.plan}
        className={`bill-tier${isCurrent ? " is-current" : ""}${plan.mostPopular ? " is-popular" : ""}${available ? " is-pickable" : ""}`}
        onClick={pick}
        // Keyboard users reach the button inside; this only has to not trap
        // them, so it takes Enter and Space the way a button would.
        role={available ? "button" : undefined}
        tabIndex={available ? 0 : undefined}
        onKeyDown={(e) => {
          if (!available) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            pick();
          }
        }}
      >
        {plan.mostPopular && <span className="bill-tier__flag">Most popular</span>}

        <div className="bill-tier__top">
          <span className={`bill-plan-option__icon is-${style.tone || "violet"}`}>
            {PlanIcon ? <PlanIcon size={20} strokeWidth={1.9} /> : null}
          </span>
          {isCurrent && (
            <span className="bill-plan-option__current">
              <Check size={13} strokeWidth={3} />
              Current
            </span>
          )}
        </div>

        <strong className="bill-tier__name">{planLabel(plan.plan)}</strong>
        <p className="bill-tier__blurb">{style.blurb}</p>

        <div className="bill-tier__price">
          {/* No price at all is the enterprise tier: quoted, not listed. */}
          {shown === null ? (
            <span className="bill-tier__custom">Custom</span>
          ) : (
            <>
              <span className="bill-tier__amount">{shown}</span>
              <span className="bill-tier__per">/ month</span>
            </>
          )}
        </div>

        <span className="bill-tier__terms">
          {shown === null
            ? "Tailored to your volume"
            : headline === 0
              ? "Free forever · no card required"
              : annual
                ? `Billed annually · ${price(plan.monthlyPrice)} billed monthly`
                : `Billed monthly · ${price(plan.annualPriceMonthly)}/mo billed annually`}
        </span>

        <ul className="bill-tier__highlights">
          {HIGHLIGHTS.map((item, i) => {
            const text = allowance(plan[item.key], item.one, item.many);
            if (!text) return null;

            const Icon = item.icon;

            return (
              <li key={`${item.key}-${i}`}>
                <Icon size={15} strokeWidth={1.9} />
                {text}
              </li>
            );
          })}
        </ul>

        <ul className="bill-tier__details">
          {DETAILS.map((item) => (
            <li key={item.key}>
              <Check size={14} strokeWidth={2.6} />
              <span>
                <strong>{formatLimit(plan[item.key])}</strong> {item.label}
              </span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          className={`bill-tier__btn${available ? " is-primary" : ""}`}
          disabled={!available || Boolean(previewing) || Boolean(busyPlan)}
          onClick={(e) => {
            // The card above handles it; without this the quote is asked for
            // twice on every click.
            e.stopPropagation();
            choose(plan);
          }}
        >
          {ctaFor(plan, isCurrent, previewing === plan.plan, otherCycle)}
          {available && <ArrowRight size={15} strokeWidth={2.4} />}
        </button>
      </div>
    );
  };

  return (
    <>
    <div className="confirm-overlay" onMouseDown={() => onClose()}>
      <div
        className="bill-plan-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-plan-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="bill-plan-modal__head">
          <div>
            <h2 id="change-plan-title" className="bill-plan-modal__title">
              Change plan
            </h2>
            <p className="bill-plan-modal__sub">
              {preview
                ? `Review your ${preview.type || "change"} before continuing.`
                : "Choose the plan that fits your team's needs."}
            </p>
          </div>
          <button
            type="button"
            className="bill-plan-modal__close"
            onClick={() => onClose()}
            aria-label="Close"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {!preview && (
        <div className="bill-cycle">
          <span className={annual ? "" : "is-active"}>Monthly</span>
          <button
            type="button"
            className={`bill-cycle__switch${annual ? " is-annual" : ""}`}
            onClick={() => setAnnual((value) => !value)}
            role="switch"
            aria-checked={annual}
            aria-label="Bill annually"
          >
            <span />
          </button>
          <span className={annual ? "is-active" : ""}>Annual</span>
          {savingPct > 0 && (
            <span className="bill-cycle__save">Save {savingPct}%</span>
          )}
        </div>
        )}

        {(error || payError) && (
          <p className="auth-error">{error || payError}</p>
        )}

        {preview ? (
          <div className="bill-preview">
            {/* Left: what is being bought, from the plan record the card was
                built from - no second request to describe it. */}
            <div className="bill-preview__plan" style={PANEL_BACKGROUND}>
              <div className="bill-preview__plan-top">
                <span
                  className={`bill-plan-option__icon is-${PLAN_STYLE[targetPlan]?.tone || "violet"}`}
                >
                  {PreviewIcon && <PreviewIcon size={22} strokeWidth={1.9} />}
                </span>

                {/* Only the types that carry one: a new subscription and a
                    no-change preview have nothing to label. */}
                {typeMeta.badge && (
                  <span className={`bill-preview__type is-${typeMeta.tone}`}>
                    {preview.type === "downgrade" ? (
                      <ArrowDownRight size={14} strokeWidth={2.4} />
                    ) : (
                      <ArrowUpRight size={14} strokeWidth={2.4} />
                    )}
                    {typeMeta.badge}
                  </span>
                )}
              </div>

              <strong className="bill-preview__plan-name">
                {toPlan?.label || `${planLabel(targetPlan)} plan`}
              </strong>
              <span className="bill-preview__cycle">
                Billed
                {" "}
                {(toPlan?.interval || preview.interval) === "annual"
                  ? "annually"
                  : "monthly"}
              </span>

              {/* The plan's own price, which is not what is charged today: an
                  upgrade mid-cycle pays a prorated amount now and this from
                  the next renewal. */}
              {typeof toPlan?.price === "number" && (
                <p className="bill-preview__plan-price">
                  <strong>{price(toPlan.price)}</strong>
                  <span>
                    / {(toPlan.interval || preview.interval) === "annual"
                      ? "year"
                      : "month"}
                  </span>
                </p>
              )}

              {PLAN_STYLE[targetPlan]?.blurb && (
                <p className="bill-preview__blurb">
                  {PLAN_STYLE[targetPlan].blurb}
                </p>
              )}

              {planRecord && (
                <>
                  <ul className="bill-preview__highlights">
                    {HIGHLIGHTS.map((item, i) => {
                      const text = allowance(
                        planRecord[item.key],
                        item.one,
                        item.many,
                      );
                      if (!text) return null;

                      const Icon = item.icon;
                      const [amount, ...rest] = text.split(" ");

                      return (
                        <li key={`${item.key}-${i}`}>
                          <span className="bill-preview__hl-icon">
                            <Icon size={15} strokeWidth={1.9} />
                          </span>
                          <span>
                            <strong>{amount}</strong>
                            {rest.join(" ")}
                          </span>
                        </li>
                      );
                    })}
                  </ul>

                  <ul className="bill-tier__details bill-preview__details">
                    {DETAILS.map((item) => (
                      <li key={item.key}>
                        <Check size={14} strokeWidth={2.6} />
                        <span>
                          <strong>{formatLimit(planRecord[item.key])}</strong>{" "}
                          {item.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* The grid is the full comparison, so "all details" is a way
                  back to it rather than a separate page. */}
              <button
                type="button"
                className="bill-preview__all"
                onClick={() => setPreview(null)}
                disabled={Boolean(busyPlan)}
              >
                <ArrowLeft size={15} strokeWidth={2.2} />
                View all plan details
              </button>

            </div>

            {/* Right: what it costs, entirely from the preview response. */}
            <div className="bill-preview__order">
              <div className="bill-preview__order-head">
                <span className="bill-preview__order-icon">
                  <ReceiptText size={20} strokeWidth={1.9} />
                </span>
                <div>
                  <h3 className="bill-preview__order-title">
                    {preview.summaryTitle || typeMeta.title || "Order summary"}
                  </h3>
                  <p className="bill-preview__order-sub">
                    Review the details below before continuing.
                  </p>
                </div>
              </div>

              {(fromPlan || toPlan) && (
                <p className="bill-preview__move">
                  <span>
                    {fromPlan?.label || "Current plan"}
                    {fromPlan?.interval && ` (${fromPlan.interval})`}
                  </span>
                  <ArrowRight size={15} strokeWidth={2.2} />
                  <strong>{toPlan?.label || planLabel(targetPlan)}</strong>
                </p>
              )}

              <ul className="bill-preview__lines">
                {(preview.lineItems || []).map((item, i) => (
                  <li key={`${item.label}-${i}`}>
                    <span>{item.label}</span>
                    <strong className={Number(item.amount) < 0 ? "is-credit" : ""}>
                      {signed(item.amount)}
                    </strong>
                  </li>
                ))}
              </ul>

              <dl className="bill-preview__totals">
                {/* Only broken out when there is tax to break out. Without
                    it the subtotal and the total are the same number, and
                    printing it twice reads as a mistake. */}
                {hasTax && (
                  <>
                    <div>
                      <dt>Subtotal</dt>
                      <dd>{signed(subtotal)}</dd>
                    </div>
                    <div>
                      {/* The rate comes with the amount, so the figure can be
                          checked rather than taken on faith. */}
                      <dt>
                        Tax
                        {typeof preview.taxRatePercent === "number" &&
                          preview.taxRatePercent > 0 &&
                          ` (${preview.taxRatePercent}%)`}
                      </dt>
                      <dd>{signed(taxAmount)}</dd>
                    </div>
                  </>
                )}

                <div className="is-total">
                  <dt>Due today</dt>
                  <dd>{signed(dueToday)}</dd>
                </div>
              </dl>

              {(preview.effectiveDate || renewalDate) && (
                <p className="bill-preview__dates">
                  <CalendarDays size={16} strokeWidth={1.9} />
                  {preview.effectiveDate && (
                    <span>Starts {plainDate(preview.effectiveDate)}</span>
                  )}
                  {preview.effectiveDate && renewalDate && (
                    <span className="bill-preview__dates-sep" />
                  )}
                  {/* nextRenewalDate is a full interval after the change, so
                      it is a different date from when the change applies -
                      reusing one field for both is what made an annual
                      downgrade read as renewing the day it started. */}
                  {/* For a locked downgrade this date is not a renewal at
                      all: it is when the plan on screen becomes available. */}
                  {renewalDate && (
                    <span>
                      {intent === ACTIONS.CANCEL ? "Available from " : "Renews "}
                      {plainDate(renewalDate)}
                    </span>
                  )}
                </p>
              )}

              {/* The backend states the change in plain language, including
                  the renewal price the line items do not carry. Shown as sent
                  rather than reworded, so the two can never disagree. */}
              {preview.message && (
                <p
                  className={`bill-preview__message${isBlocked ? " is-blocked" : ""}`}
                  role={isBlocked ? "alert" : undefined}
                >
                  {preview.message}
                </p>
              )}

              {/* The plan holds fewer assistants than the account runs, so
                  the server will not sell it yet. The list is the way out:
                  each one removed is re-counted server side, and the quote is
                  asked for again. */}
              {overAssistantLimit && (
                <div className="bill-preview__req">
                  <p className="bill-preview__req-head">
                    <AlertTriangle size={16} strokeWidth={2.1} />
                    <span>
                      Remove {assistantsOver.toDeactivate}{" "}
                      {assistantsOver.toDeactivate === 1
                        ? "assistant"
                        : "assistants"}{" "}
                      to continue
                    </span>
                    <strong>
                      {assistantsOver.active} / {assistantsOver.allowed}
                    </strong>
                  </p>

                  <ul className="bill-preview__req-list">
                    {(assistantsOver.list || []).map((item) => (
                      <li key={item.id}>
                        <span className="bill-preview__req-name">
                          <strong>{item.name || "Untitled assistant"}</strong>
                          {item.sourceUrl && <span>{item.sourceUrl}</span>}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Said as steps, because it is three screens away and the
                      count is checked again on the way back. */}
                  <ol className="bill-preview__req-steps">
                    <li>
                      Open <strong>Install &amp; Settings</strong> and pick an
                      assistant in the sidebar switcher.
                    </li>
                    <li>
                      Use <strong>Delete assistant</strong> at the bottom of the
                      page. Its widget stops loading straight away.
                    </li>
                    <li>
                      Repeat until{" "}
                      <strong>
                        {assistantsOver.allowed}{" "}
                        {assistantsOver.allowed === 1
                          ? "assistant is"
                          : "assistants are"}
                      </strong>{" "}
                      left, then come back and choose this plan again.
                    </li>
                  </ol>

                  <button
                    type="button"
                    className="bill-preview__req-go"
                    onClick={goToInstallSettings}
                  >
                    <Trash2 size={15} strokeWidth={2} />
                    Go to Install &amp; Settings
                    <ArrowRight size={15} strokeWidth={2.4} />
                  </button>
                </div>
              )}

              {/* The audit trail behind the numbers: available, but not in
                  the way of the decision being made. */}
              {Array.isArray(preview.calculation) &&
                preview.calculation.length > 0 && (
                  <details className="bill-preview__calc" open>
                    <summary>How is this calculated?</summary>
                    <ul>
                      {preview.calculation.map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  </details>
                )}

              {/* Whatever the server put on it. A locked downgrade reads
                  "Available after <date>" and stays disabled - the button
                  below it is the one that leads anywhere. */}
              <button
                type="button"
                className="bill-preview__pay"
                onClick={confirm}
                disabled={
                  Boolean(busyPlan) ||
                  overAssistantLimit ||
                  (intent === ACTIONS.CANCEL ? false : actionDisabled)
                }
              >
                {busyPlan ? (
                  <span className="install-status-spinner" aria-hidden="true" />
                ) : (
                  <Lock size={16} strokeWidth={2.2} />
                )}
                {payStage === "card"
                  ? "Waiting for the card…"
                  : payStage === "confirming"
                    ? "Confirming payment…"
                    : busyPlan
                      ? "Starting…"
                      : intent === ACTIONS.CANCEL
                        ? "Turn off auto-renewal"
                        : action?.label || `Confirm and pay ${signed(dueToday)}`}
                {!busyPlan && <ArrowRight size={16} strokeWidth={2.4} />}
              </button>

              {/* The server's own words for why the button above is the one
                  it is, kept next to it rather than only in the message. */}
              {intent === ACTIONS.CANCEL && action?.label && (
                <p className="bill-preview__locked">{action.label}</p>
              )}

              <p className="bill-preview__trust">
                <span>
                  <ShieldCheck size={15} strokeWidth={1.9} />
                  Secure payment
                </span>
                <span>
                  <Lock size={14} strokeWidth={1.9} />
                  Your data is safe
                </span>
                <span>Card payments</span>
              </p>
            </div>
          </div>
        ) : loading && plans.length === 0 ? (
          <LoadingState title="Loading plans" inline />
        ) : (
          <div className="bill-tiers">{plans.map(renderCard)}</div>
        )}
      </div>
    </div>

    {/* Outside the modal's own overlay on purpose: nested, a click on this
        backdrop would bubble through and close the plan modal as well. */}
    <ConfirmDialog
      open={askPay}
      tone="primary"
      title={payTitle}
      message={payMessage}
      confirmLabel={busyPlan ? "Starting…" : action?.label || "Yes, continue"}
      cancelLabel="Not now"
      onConfirm={runPayment}
      onCancel={() => !busyPlan && setAskPay(false)}
    />

    <ConfirmDialog
      open={askCancel}
      title="Turn off auto-renewal?"
      message={
        preview?.message ||
        "This stops all future payments. Your plan keeps working until the end of the period you have already paid for."
      }
      confirmLabel={busyPlan ? "Turning off…" : "Yes, turn it off"}
      cancelLabel="Keep auto-renewal"
      onConfirm={turnOffAutoRenewal}
      onCancel={() => !busyPlan && setAskCancel(false)}
    />
    </>
  );
}
